/**
 * Saving a scenario as an Excel workbook and opening it again.
 *
 * The "Scenario" and "Inputs" sheets are what is read back, so a scenario can
 * be shared, or its inputs edited in Excel, and recalculated later against the
 * governed baseline. The "Results" sheet is a record of what the scenario
 * showed when it was saved; it is never read back.
 */
import type {
  ScenarioLeverUnit,
  ScenarioResult,
  ScenarioTemplate,
  ScenarioValueUnit,
  WhatIfPeriod,
} from "../../services/api";
import { PERIOD_LABELS } from "./scenarioFormat";
import { defaultLevers, normaliseLever } from "./scenarioLayout";
import {
  WorkbookReadError,
  readWorkbook,
  writeWorkbook,
  type CellValue,
  type Sheet,
  type SheetRow,
} from "./xlsx";

export const SCENARIO_FILE_FORMAT = "dmi-what-if-scenario";
export const SCENARIO_FILE_VERSION = 1;

const SCENARIO_SHEET = "Scenario";
const INPUTS_SHEET = "Inputs";
const RESULTS_SHEET = "Results";

const FIELDS = {
  format: "Format",
  version: "Format version",
  templateId: "Scenario ID",
  templateTitle: "Scenario",
  contextId: "Context ID",
  contextVersion: "Context version",
  period: "Baseline period",
  breakdown: "Break down by",
  savedAt: "Saved at",
} as const;

const INPUT_HEADER = ["Lever ID", "Lever", "Value", "Unit", "Minimum", "Maximum", "Group"];

const UNIT_LABELS: Record<ScenarioLeverUnit | ScenarioValueUnit, string> = {
  currency: "USD",
  currency_per_hour: "USD per hour",
  hours: "hours",
  percent: "percent",
  points: "points",
  count: "count",
  months: "months",
  seconds: "seconds",
};

/** The inputs that define a scenario, independent of its calculated result. */
export interface ScenarioInputs {
  period: WhatIfPeriod;
  breakdown: string;
  levers: Record<string, number>;
}

export interface SavedScenario extends ScenarioInputs {
  templateId: string;
  templateTitle: string;
  contextId: string;
  contextVersion: string | null;
  savedAt: string;
}

export class ScenarioFileError extends Error {}

function text(value: CellValue | undefined): string {
  return value == null ? "" : String(value).trim();
}

function header(cells: CellValue[]): SheetRow {
  return { cells, bold: true };
}

function resultRows(result: ScenarioResult, template: ScenarioTemplate): SheetRow[] {
  const rows: SheetRow[] = [
    header([result.title]),
    { cells: [result.headline] },
    { cells: [] },
    header(["Measure", "Unit", "Today", "Scenario", "Change"]),
    ...result.kpis.map((kpi) => ({
      cells: [kpi.label, UNIT_LABELS[kpi.unit], kpi.baseline ?? null, kpi.scenario ?? null, kpi.delta ?? null],
    })),
  ];

  if (result.bridge.length > 0) {
    rows.push(
      { cells: [] },
      header([result.bridgeTitle ?? "Bridge", "Unit", "Value", "Kind"]),
      ...result.bridge.map((step) => ({
        cells: [step.label, UNIT_LABELS[result.bridgeUnit ?? "currency"], step.value, step.kind],
      })),
    );
  }

  const table = result.table;
  if (table) {
    rows.push(
      { cells: [] },
      header([table.title]),
      header([table.dimensionLabel, ...table.columns.map((column) => `${column.label} (${UNIT_LABELS[column.unit]})`)]),
      ...table.rows.map((row) => ({
        cells: [
          text(row.label) || "Not set",
          ...table.columns.map((column) => {
            const value = row[column.key];
            return typeof value === "number" ? value : null;
          }),
        ],
      })),
    );
  }

  const trend = result.trend;
  if (trend && trend.points.length > 0) {
    rows.push(
      { cells: [] },
      header([`${trend.title} (${UNIT_LABELS[trend.unit]})`]),
      header(["Period", "Today", "Scenario"]),
      ...trend.points.map((point) => ({
        cells: [point.label, point.baseline ?? null, point.scenario ?? null],
      })),
    );
  }

  const sensitivity = result.sensitivity;
  if (sensitivity) {
    rows.push(
      { cells: [] },
      header([`${sensitivity.title} (${UNIT_LABELS[sensitivity.valueUnit]})`]),
      header([
        `${sensitivity.rowLabel} \\ ${sensitivity.columnLabel}`,
        ...sensitivity.columns,
      ]),
      ...sensitivity.rows.map((rowValue, index) => ({
        cells: [rowValue, ...(sensitivity.values[index] ?? [])],
      })),
    );
  }

  const lists: [string, string[]][] = [
    ["What this tells you", result.insights],
    ["Warnings", result.warnings],
    ["Assumptions", result.assumptions],
  ];
  for (const [title, items] of lists) {
    if (items.length === 0) continue;
    rows.push({ cells: [] }, header([title]), ...items.map((item) => ({ cells: [item] })));
  }

  const breakdownLabel = template.breakdowns.find(
    (item) => item.member === result.breakdown,
  )?.label;
  rows.push(
    { cells: [] },
    header(["Provenance"]),
    { cells: ["Baseline period", PERIOD_LABELS[result.period]] },
    { cells: ["Break down by", breakdownLabel ?? "Portfolio total"] },
    { cells: ["Context", `${result.provenance.contextId} v${result.provenance.contextVersion}`] },
    { cells: ["Baseline queries", result.provenance.baselineQueryIds.join(", ")] },
    { cells: ["Baseline executed at", result.provenance.baselineExecutedAt] },
    { cells: ["Method", result.provenance.method] },
    { cells: ["Scenario hash", result.provenance.specHash] },
    { cells: [] },
    { cells: [result.disclaimer] },
  );
  return rows;
}

/**
 * Builds the workbook for a scenario. The result is included only when it was
 * calculated from exactly these inputs, so the file never disagrees with itself.
 */
export function scenarioWorkbook(
  saved: SavedScenario,
  template: ScenarioTemplate,
  result: ScenarioResult | null,
): Uint8Array {
  const breakdownLabel = template.breakdowns.find(
    (item) => item.member === saved.breakdown,
  )?.label;
  const scenario: Sheet = {
    name: SCENARIO_SHEET,
    widths: [20, 40, 40],
    rows: [
      header(["Field", "Value", "Description"]),
      { cells: [FIELDS.format, SCENARIO_FILE_FORMAT, "Identifies a saved What-If Studio scenario"] },
      { cells: [FIELDS.version, SCENARIO_FILE_VERSION] },
      { cells: [FIELDS.templateId, saved.templateId, template.question] },
      { cells: [FIELDS.templateTitle, saved.templateTitle] },
      { cells: [FIELDS.contextId, saved.contextId] },
      { cells: [FIELDS.contextVersion, saved.contextVersion ?? ""] },
      {
        cells: [
          FIELDS.period,
          saved.period,
          `${PERIOD_LABELS[saved.period]}. One of: ${template.periods.join(", ")}`,
        ],
      },
      {
        cells: [
          FIELDS.breakdown,
          saved.breakdown,
          breakdownLabel ?? "Portfolio total",
        ],
      },
      { cells: [FIELDS.savedAt, saved.savedAt] },
    ],
  };
  const inputs: Sheet = {
    name: INPUTS_SHEET,
    widths: [24, 32, 12, 14, 12, 12, 12],
    rows: [
      header(INPUT_HEADER),
      ...template.levers.map((lever) => ({
        cells: [
          lever.id,
          lever.label,
          saved.levers[lever.id] ?? lever.default,
          UNIT_LABELS[lever.unit],
          lever.minimum,
          lever.maximum,
          lever.group,
        ],
      })),
    ],
  };
  const sheets = [scenario, inputs];
  if (result) {
    sheets.push({ name: RESULTS_SHEET, widths: [40, 18, 18, 18, 18], rows: resultRows(result, template) });
  }
  return writeWorkbook(sheets);
}

/** Accepts the period's label too, since that is what a person types in Excel. */
function periodId(value: string): WhatIfPeriod {
  const byLabel = Object.entries(PERIOD_LABELS).find(
    ([, label]) => label.toLowerCase() === value.toLowerCase(),
  );
  return (byLabel?.[0] ?? value) as WhatIfPeriod;
}

/** Reads the scenario a workbook was saved with. Does not check it against a template. */
export function readScenarioWorkbook(bytes: Uint8Array): SavedScenario {
  let sheets: Map<string, CellValue[][]>;
  try {
    sheets = readWorkbook(bytes);
  } catch (caught) {
    if (caught instanceof WorkbookReadError) throw new ScenarioFileError(caught.message);
    throw caught;
  }

  const scenarioRows = sheets.get(SCENARIO_SHEET);
  const inputRows = sheets.get(INPUTS_SHEET);
  if (!scenarioRows || !inputRows) {
    throw new ScenarioFileError(
      `This workbook is not a saved scenario. It needs "${SCENARIO_SHEET}" and "${INPUTS_SHEET}" sheets.`,
    );
  }

  const fields = new Map<string, string>();
  for (const row of scenarioRows) {
    const name = text(row[0]);
    if (name) fields.set(name, text(row[1]));
  }
  if (fields.get(FIELDS.format) !== SCENARIO_FILE_FORMAT) {
    throw new ScenarioFileError("This workbook was not saved from the What-If Studio.");
  }
  const version = Number(fields.get(FIELDS.version));
  if (!Number.isFinite(version) || version > SCENARIO_FILE_VERSION) {
    throw new ScenarioFileError(
      "This scenario was saved by a newer version of the studio and cannot be opened here.",
    );
  }
  const templateId = fields.get(FIELDS.templateId) ?? "";
  if (!templateId) {
    throw new ScenarioFileError(`The "${FIELDS.templateId}" field is missing.`);
  }

  const headerIndex = inputRows.findIndex((row) => text(row[0]) === INPUT_HEADER[0]);
  const valueColumn = (inputRows[headerIndex] ?? []).findIndex((cell) => text(cell) === "Value");
  if (valueColumn < 0) {
    throw new ScenarioFileError(`The "${INPUTS_SHEET}" sheet needs "Lever ID" and "Value" columns.`);
  }
  const levers: Record<string, number> = {};
  for (const row of inputRows.slice(headerIndex + 1)) {
    const id = text(row[0]);
    if (!id) continue;
    const raw = row[valueColumn];
    const value = typeof raw === "number" ? raw : Number(text(raw));
    if (text(raw) === "" || !Number.isFinite(value)) {
      throw new ScenarioFileError(`The value for "${text(row[1]) || id}" is not a number.`);
    }
    levers[id] = value;
  }

  return {
    templateId,
    templateTitle: fields.get(FIELDS.templateTitle) || templateId,
    contextId: fields.get(FIELDS.contextId) ?? "",
    contextVersion: fields.get(FIELDS.contextVersion) || null,
    period: periodId(fields.get(FIELDS.period) ?? ""),
    breakdown: fields.get(FIELDS.breakdown) ?? "",
    levers,
    savedAt: fields.get(FIELDS.savedAt) ?? "",
  };
}

/**
 * Fits a saved scenario to the template as it is today. Values outside a
 * lever's range, or a period or breakdown the template no longer offers, fall
 * back to something valid, and each such change is reported.
 */
export function fitSavedScenario(
  template: ScenarioTemplate,
  saved: SavedScenario,
): { inputs: ScenarioInputs; adjusted: string[] } {
  const adjusted: string[] = [];
  const levers = defaultLevers(template);
  for (const lever of template.levers) {
    const raw = saved.levers[lever.id];
    if (raw === undefined) {
      adjusted.push(`${lever.label} was not in the file and uses its default`);
      continue;
    }
    const value = normaliseLever(lever, raw);
    if (value !== raw) adjusted.push(`${lever.label} was moved into its range`);
    levers[lever.id] = value;
  }

  let period = saved.period;
  if (!template.periods.includes(period)) {
    adjusted.push(`the baseline period uses ${PERIOD_LABELS[template.defaultPeriod]}`);
    period = template.defaultPeriod;
  }

  let breakdown = saved.breakdown;
  const offered = template.breakdowns.some((item) => item.member === breakdown);
  if (!offered && (breakdown !== "" || template.breakdownRequired)) {
    const fallback = template.breakdowns[0];
    breakdown = template.breakdownRequired ? (fallback?.member ?? "") : "";
    adjusted.push(
      `the breakdown uses ${template.breakdownRequired ? (fallback?.label ?? "the default") : "the portfolio total"}`,
    );
  }

  return { inputs: { period, breakdown, levers }, adjusted };
}

/** A file name such as "Utilization uplift scenario 2026-10-06.xlsx". */
export function scenarioFileName(title: string, savedAt: string): string {
  const safe = title.replace(/[<>:"/\\|?*]/g, "").replace(/\s+/g, " ").trim() || "Scenario";
  return `${safe} scenario ${savedAt.slice(0, 10)}.xlsx`;
}
